import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Table,
  Button,
  Row,
  Col,
  Typography,
} from "antd";
import {
  CreditCardOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import { bankRecDate as convertArrayToDayjs } from "@/views/BankReconciliation/BankRec/dates";
import commonFn from "@/utils/commonFn";

/* ---------------- 组件外的静态内容 ---------------- */
const columnStyle = {
  padding: 20,
  background: "#fff",
  border: "1px solid #f0f0f0",
  borderRadius: 6,
  height: "100%",
};

const STATUS_STYLE = {
  success: {
    title: "Success",
    color: "#52c41a",
    bg: "#f6ffed",
    border: "#b7eb8f",
    Icon: CheckCircleFilled,
  },
  fail: {
    title: "Failed",
    color: "#ff4d4f",
    bg: "#fff2f0",
    border: "#ffccc7",
    Icon: CloseCircleFilled,
  },
};
const SectionTitle = ({ children }) => (
  <div
    style={{
      fontSize: 16,
      fontWeight: 600,
      marginBottom: 20,
      color: "#262626",
    }}
  >
    {children}
  </div>
);

const InfoItem = ({ label, valueStyle, children }) => (
  <div style={{ display: "flex", alignItems: "baseline", marginBottom: 16 }}>
    <div style={{ width: 90, flexShrink: 0, color: "#8c8c8c" }}>{label}</div>
    <div
      style={{
        fontWeight: 500,
        color: "#262626",
        minWidth: 0,
        wordBreak: "break-all",
        ...valueStyle,
      }}
    >
      {children}
    </div>
  </div>
);

const StatusBanner = ({ success, msg }) => {
  const s = success ? STATUS_STYLE.success : STATUS_STYLE.fail;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 12px",
        marginBottom: 20,
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderRadius: 6,
      }}
    >
      <s.Icon style={{ color: s.color, fontSize: 18, marginTop: 2 }} />
      <div>
        <div style={{ color: s.color, fontWeight: 600 }}>{s.title}</div>
        {/* 只有失败时才显示 msg */}
        {!success && (
          <div style={{ color: "#595959", fontSize: 13, marginTop: 2 }}>
            {msg || "Submit failed, please try again."}
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------------- 主组件（批量） ---------------- */

// records：勾选的多条记录，只允许修改 GL Account 和 Remark，
// 其余参数（propId 等）原样取自每条记录
const RecordCreditBatchModal = ({ visible, records, onClose, onSuccess, glChartNameId,  }) => {
  const [form] = Form.useForm();
  const [glAcctList, setGlAcctList] = useState([]);
  const [loadingGlAcct, setLoadingGlAcct] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const list = records || [];
  const first = list[0];
  const totalAmount = Number(
    list.reduce((sum, r) => sum + (Number(r.amount) || 0), 0).toFixed(2),
  );
  // 打开弹窗：直接用记录里的 propId 获取 GL Account，不再请求 Prop 列表
  useEffect(() => {
    if (!visible || !first) {
      return;
    }
    let active = true;

    form.resetFields();
    setSubmitResult(null);
    setGlAcctList([]);

  const loadGlAcct = async () => {
  setLoadingGlAcct(true);
  try {
     const response = await http.get(api.getCreditGIAcct, {
       params: {
         propId: undefined,
         glChartNameId
         // acctType: ["C", "E"].toString(),
         // active: 1,
       },
     });
    if (response?.code === "1") {
      setGlAcctList(
        (response.data || []).map((item) => ({
          value: item.glAcct,
          label: item.glAcctTitle,
          title: item.title,
        })),
      );
    } else {
      setGlAcctList([]);
      // commonFn.modalFailedFn(response);
    }
  } catch (err) {
    console.log(err);
    if (active) {
      setGlAcctList([]);
    }
  } finally {
    if (active) {
      setLoadingGlAcct(false);
    }
  }
};

    loadGlAcct();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, records, form]);

  const handleClose = () => {
    form.resetFields();
    setGlAcctList([]);
    setSubmitResult(null);
    onClose?.();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const values = await form.validateFields();
      const params = list.map((r) => ({
        date1: convertArrayToDayjs(r.date)?.format("YYYY-MM-DD"),
        amount: r.amount,
        propId: r.propId, 
        glAcct: values.glAcct,
        remark: values.remark?.trim() || r.remark, 
        checkNo: r.checkNo,
        bankId: r.bankId,
        bankTransNewId: r.bankTransNewId,
      }));

      const res = await http.post(api.getBatchRecordCredit, params);
      const isSuccess = res?.code === "1" || res?.code === 1;

      setSubmitResult({
        success: isSuccess,
        msg: res?.msg,
        ...(isSuccess ? res?.data : {}),
      });
      if (isSuccess) {
        onSuccess?.(res);
      }
    } catch (err) {
      if (err?.errorFields) {
        return;
      }
      setSubmitResult({ success: false, msg: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const resultData = submitResult;


  // batch / matchId 返回的是数组
const toText = (v) => (Array.isArray(v) ? v.join(", ") : (v ?? " "));
const batchColumns = [
  {
    title: "Date",
    dataIndex: "date",
    width: 100,
    render: (v) => convertArrayToDayjs(v)?.format("MM/DD/YYYY"),
  },
  { title: "Check #", dataIndex: "checkNo", width:80 },
  { title: "Description", dataIndex: "remark", width:300  },
  { title: "Account #", dataIndex: "bankAccount",filterKey: "bankAccount", width: 100},
  { title: "Account Remark", dataIndex: "bankRemark", width:150 },
      {
      title: "Entity",
      dataIndex: "trust",
      filterKey: "trust",
      width:200,
      render: (text, record) => {
        if (record?.trustName && record?.trust) {
          return (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span>{record.trustName}</span>
              <span>({record.trust})</span>
            </div>
          );
        }
        return record?.trust || record?.trustName || "";
      },
    },
  {
    title: "Amount",
    dataIndex: "amount",
    width: 100,
    align: "right",
    render: (v) => commonFn.amountToDollarBracketFn(v),
  },
];


  return (
    <Modal
      title={
        <Row justify="start" align="middle" className="model_title">
          <Col>
            <CreditCardOutlined className="model_title_mr10" />
          </Col>
          <Col>
            <Typography.Title level={4} className="mg" style={{ margin: 0 }}>
              BATCH CASH RECEIVE
            </Typography.Title>
          </Col>
        </Row>
      }
      open={visible}
      maskClosable={false}
      forceRender
      width="60%"
      styles={{ body: { maxHeight: "70vh", overflowY: "auto", paddingTop: 8 } }}
      onCancel={handleClose}
      footer={
        <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button  type="primary" loading={submitting}
            disabled={!!submitResult?.success}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div>
      }
    >
      <div className="model_top" style={{ width: "100%" }}>
        <Form
          form={form}
          layout="horizontal"
           labelCol={{ span: 5 }}
          wrapperCol={{ span: 19 }}
          size="middle"
          style={{ width: "100%" }}
        >
          <Row gutter={24} style={{ width: "100%", margin: 0 }}>
            {/* 左侧：Cash Receive Detail */}
            <Col span={20}>
              <div style={columnStyle}>
                <SectionTitle>Cash Receive Detail</SectionTitle>

                {/* 选中的交易明细 + 合计 */}
                <Table
                  rowClassName="table-row-gradient"
                  size="small"
                  bordered
                  rowKey="bankTransNewId"
                  columns={batchColumns}
                  dataSource={list}
                  pagination={false}
                  style={{ marginBottom: 24 }}
                  summary={() => (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={6}>
                          <b>Total ({list.length} transactions)</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <b>{commonFn.amountToDollarBracketFn(totalAmount)}</b>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />

                {/* 只有 GL Account 和 Remark 可修改 */}
                <Form.Item
                  name="glAcct"
                  label="GL Account"
                  rules={[
                    { required: true, message: "GL Account is required!" },
                  ]}
                >
                  <Select
                    showSearch
                    allowClear
                    loading={loadingGlAcct}
                    optionFilterProp="label"
                    options={glAcctList}
                  />
                </Form.Item>

                <Form.Item name="remark" label="Remark">
                  <Input
                    placeholder="Leave empty to keep each transaction's own remark"
                    allowClear
                  />
                </Form.Item>
              </div>
            </Col>

            {/* 右侧：Transaction Information */}
            <Col span={4}>
              <div style={columnStyle}>
                <SectionTitle>Transaction Information</SectionTitle>
                {resultData ? (
                  <>
                    <StatusBanner
                      success={resultData.success}
                      msg={resultData.msg}
                    />
                    {resultData.success && (
                      <>
                        <InfoItem label="Count">
                          {resultData.count ?? "-"}
                        </InfoItem>
                        <InfoItem label="Batch">
                          {toText(resultData.batch)}
                        </InfoItem>
                        <InfoItem label="Match ID">
                          {toText(resultData.matchId)}
                        </InfoItem>
                      </>
                    )}
                  </>
                ) : (
                  <div
                    style={{
                      color: "#bfbfbf",
                      fontSize: 14,
                      textAlign: "center",
                      paddingTop: 40,
                    }}
                  >
                    Transaction information will appear after submission.
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default RecordCreditBatchModal;
